import { createContext, useEffect, useState, useCallback, useContext } from 'react'
import cookies from 'js-cookie'
import { decodeGlobalId } from '~/utils/id'
import { useLoggedInUser } from '~/components/LoggedInUserContext'

// The following function content is copy pasted from
// https://developers.intercom.com/installing-intercom/docs/basic-javascript
// We keep it like this so we can easily copy paste an update if needed.
const initializeIntercom = appId => {
  const intercom = window.Intercom
  if (typeof intercom === 'function') {
    intercom('reattach_activator')
    intercom('update', window.intercomSettings)
  } else {
    const instance = function () {
      instance.c(arguments)
    }
    instance.q = []
    instance.c = function (args) {
      instance.q.push(args)
    }
    window.Intercom = instance
    const load = function () {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = `https://widget.intercom.io/widget/${appId}`
      const element = document.getElementsByTagName('script')[0]
      if (element) {
        element.parentNode.insertBefore(script, element)
      }
    }
    if (document.readyState === 'complete') {
      load()
    } else if (window.attachEvent) {
      window.attachEvent('onload', load)
    } else {
      window.addEventListener('load', load, false)
    }
  }
}

const IntercomContext = createContext()
export const IntercomConsumer = IntercomContext.Consumer

export function useIntercom() {
  const context = useContext(IntercomContext)

  if (!context) {
    throw new Error('useIntercom() must be used within a IntercomProvider')
  }

  return context
}

export function IntercomProvider({ children, appId }) {
  const defaultEngaged = cookies.get('Intercom-engaged') === '1'
  const [engaged, setEngaged] = useState(defaultEngaged)
  const showChat = cookies.get('Intercom-show') === '1'
  const { loggedInUser } = useLoggedInUser()

  const { id, firstname: name, email, intercomVerificationId: user_hash } = loggedInUser || {}
  const options = loggedInUser
    ? {
        user_id: decodeGlobalId(id),
        name,
        email,
        user_hash,
        hide_default_launcher: !engaged,
      }
    : {
        hide_default_launcher: !engaged,
      }

  const initialize = useCallback(() => {
    if (!window.Intercom) {
      initializeIntercom(appId)
    }
  }, [appId])

  const boot = useCallback(() => {
    window.Intercom('boot', {
      app_id: appId,
      ...options,
    })
  }, [appId, options])

  const update = useCallback(() => {
    window.Intercom('update', {
      ...options,
    })
  }, [options])

  const bootOrUpdate = useCallback(() => {
    if (!window.Intercom.booted) {
      boot()
    } else {
      update()
    }
  }, [boot, update])

  const setupEventHandlers = useCallback(() => {
    window.Intercom('onHide', () => cookies.set('Intercom-show', '0'))
    window.Intercom('onShow', () => cookies.set('Intercom-show', '1'))
  }, [])

  const openChat = useCallback(() => {
    // Update engaged cookie for default value in state
    cookies.set('Intercom-engaged', '1')
    // Update current engaged value
    setEngaged(true)
    window.Intercom('show')
  }, [])

  const restart = useCallback(() => {
    // Remove current user session (for when user is on shared device)
    window.Intercom('shutdown')

    // Boot new session
    window.Intercom('boot', {
      app_id: appId,
      hide_default_launcher: true,
    })

    // Remove the intercom "bubble" at the bottom of the page
    cookies.set('Intercom-engaged', '0')
    setEngaged(false)
  }, [appId])

  const trackEvent = useCallback((event, metadata) => {
    if (typeof window.Intercom === 'undefined') return

    window.Intercom('trackEvent', event, metadata)
  }, [])

  useEffect(() => {
    initialize()
    bootOrUpdate()
    setupEventHandlers()

    if (showChat) {
      openChat()
    }
  }, [initialize, bootOrUpdate, setupEventHandlers, showChat, openChat])

  const start = () => {
    bootOrUpdate()
    openChat()
  }

  return (
    <IntercomContext.Provider value={{ start, restart, trackEvent }}>
      {children}
    </IntercomContext.Provider>
  )
}
